import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { useEffect } from 'react';

import type { LensDivProps } from '../../types';
import { getColorValue, getShadow, u } from '../../utilities';

import { SvgClose } from '../icon/available-icons/close';
import IconButton from '../icon-button/icon-button';

const WEBAPP = 'web-app';
const CHROME_EXTENSION = 'chrome-extension';
const toastDurations = { short: 3000, medium: 5000, long: 8000 };

const enter = (toastDuration, platform) => keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, ${u(platform === CHROME_EXTENSION ? -8 : 8)});
  }
  // (300 / toastDuration) * 100 evaluates to 10% for short. Longer durations will have the same speed of animation
  ${(300 / toastDuration) * 100}% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  // 100 - (300 / toastDuration) * 100 evaluates to 90% for short. Longer durations will have the same speed of animation
  ${100 - (300 / toastDuration) * 100}% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const getTop = platform => {
  switch (platform) {
    case WEBAPP:
      return 'unset';
    case CHROME_EXTENSION:
      return u(4);
    default:
      return 'unset';
  }
};

const getBottom = platform => {
  switch (platform) {
    case WEBAPP:
      return u(4);
    case CHROME_EXTENSION:
      return 'unset';
    default:
      return u(4);
  }
};

const ToastWrapper = styled.div<ToastWrapperProps>`
  animation: ${props => enter(props.toastDuration, props.platform)}
    ${props => props.toastDuration}ms forwards;
  background-color: ${getColorValue('backgroundInverse')};
  border-radius: var(--ds-radius-large);
  top: ${props => getTop(props.platform)};
  bottom: ${props => getBottom(props.platform)};
  ${getShadow('large')};
  color: ${getColorValue('bodyInverse')};
  display: grid;
  grid-auto-flow: column;
  gap: var(--lns-space-small);
  justify-content: space-between;
  left: 50%;
  max-width: ${u(50)}; // Fallback when min() function is not supported
  max-width: min(90%, ${u(50)});
  padding: ${u(1.5)} var(--ds-space-150) ${u(1.5)} var(--ds-space-200);
  position: fixed;
  transform: translateX(-50%);
  width: max-content;
  z-index: ${props => props.zIndex};
`;

const ChildrenSection = styled.div`
  align-self: center;
`;

const Toast = ({
  children,
  isOpen,
  onCloseClick,
  zIndex = 1100,
  duration = 'short',
  platform = 'web-app',
}: ToastProps &
  React.ComponentProps<typeof ToastWrapper> &
  Omit<
    LensDivProps,
    keyof ToastProps & React.ComponentProps<typeof ToastWrapper>
  >) => {
  const toastDuration = toastDurations[duration];

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) {
        onCloseClick();
      }
    }, toastDuration);

    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <ToastWrapper
          role="presentation"
          onClick={e => e.stopPropagation()}
          zIndex={zIndex}
          isOpen={isOpen}
          toastDuration={toastDuration}
          platform={platform}
        >
          <ChildrenSection aria-live="polite">{children}</ChildrenSection>
          {onCloseClick && (
            <IconButton
              altText="Close"
              icon={<SvgClose />}
              onClick={onCloseClick}
              iconColor="bodyInverse"
            />
          )}
        </ToastWrapper>
      )}
    </>
  );
};

type ToastProps = {
  children?: React.ReactNode;
  isOpen?: boolean;
  onCloseClick?: () => void;
  zIndex?: number;
  duration?: 'short' | 'medium' | 'long';
  platform?: 'web-app' | 'chrome-extension';
};

type ToastWrapperProps = {
  zIndex?: number;
  isOpen?: boolean;
  toastDuration?: number;
  platform?: 'web-app' | 'chrome-extension';
};

export default Toast;
